import React, { useEffect, useState } from 'react'
import { ProjectProps } from '../Project'
import { useTranslation } from 'react-i18next';
import Loading from '../../../components/Loading';
import { TbExternalLink } from 'react-icons/tb';
import { DatabaseModel } from '../../../utils/interfaces';
import { getStandardTime, isValidServiceName, useAsyncEffect, useEventListener } from '../../../utils/functions';
import { Admin, createItem, deleteItems, getItems } from '../../../api/API';
import { freeLimits, proLimits, tables } from '../../../utils/constants';
import { useNavigate } from 'react-router-dom';
import MainBlock from '../../../components/MainBlock';
import Table from '../../../components/Table';
import Alert from '../../../components/Alert';
import AlertWrapper from '../../../components/AlertWrapper';
import { Input } from '../../../components/Input';
import { Title } from '../../../components/General';

const Database = (props: ProjectProps) => {

  const { t } = useTranslation();
  const navigate = useNavigate();
  const [databases, setDatabases] = useState<DatabaseModel[]>([]);
  const [dbInfos, setDbInfos] = useState<Record<string, any>[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMetaLoading, setIsMetaLoading] = useState(true);
  const [showDeleteDatabasesAlert, setShowDeleteDatabasesAlert] = useState(false);
  const [showAddDatabaseAlert, setShowAddDatabaseAlert] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [newDatabaseName, setNewDatabaseName] = useState('');

  // event handlers
  useEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (!target.closest('.add-database-button') && !target.closest('.add-database-alert')) {
      setShowAddDatabaseAlert(false);
    }
    if (!target.closest('.delete-databases-button') && !target.closest('.delete-databases-alert')) {
      setShowDeleteDatabasesAlert(false);
    }
  })

  useEffect(() => {
    setNewDatabaseName('');
  }, [showAddDatabaseAlert]);

  // update metadata when databases change
  useAsyncEffect(async () => {
    let filtered: Record<string, any>[] = [];
    for (const db of databases) {
      filtered.push({ [t('table-attr.general.name')]: db.name, [t('table-attr.general.created-at')]: getStandardTime(new Date(db.createdAt)) });
    }
    setDbInfos(filtered);
    if (!isLoading) setIsMetaLoading(false);
  }, [databases]);

  useAsyncEffect(async () => {
    const data: DatabaseModel[] = (await getItems(tables.databases, { 'projectId': props.project.id })).items || [];
    if (data) {
      setDatabases(data.sort((a, b) => a.name < b.name ? -1 : 1));
      setIsLoading(false);
    }
  }, [props.project]);

  // check limit
  const hasReachedLimit = () => {
    return props.user?.plan.name === 'pro' ? 
      databases.length >= proLimits.databases 
      : props.user?.plan.name === 'free' ? databases.length >= freeLimits.databases
      : false;
  }

  // add new database
  const addNewDatabase = async () => {
    setIsAdding(true);
    const newDatabase: DatabaseModel = { id: `${props.project.id}_${newDatabaseName}`, name: newDatabaseName, userId: props.user!.id, projectId: props.project.id, description: '', adminIds: [{ userId: props.user!.id, access: 'full_access' }], createdAt: new Date().toISOString(), lastUpdate: new Date().toISOString() };
    await createItem(tables.databases, newDatabase);
    await Admin.createDatabase(newDatabase.id);
    setDatabases(prev => [...prev, newDatabase]);
    setShowAddDatabaseAlert(false);
    setIsAdding(false);
  }

  // delete databases
  const deleteDatabases = async () => {
    setIsDeleting(true);
    for (const name of selected) {
      await Admin.deleteDatabase(`${props.project.id}_${name}`);
    }
    await deleteItems(tables.databases, selected.map(name => `${props.project.id}_${name}`));
    setDatabases(databases.filter(db => !selected.includes(db.name)));
    setSelected([]);
    setIsDeleting(false);
    setShowDeleteDatabasesAlert(false);
  }

  return (
    isMetaLoading ? <main className='flex items-center justify-center flex-1'><Loading solid size={8} /></main>
      : <>
        <div className='flex-1 flex flex-col p-4 md:px-8 overflow-auto'>
          <div className='flex items-center justify-between gap-4'>
            <Title>{t('sidebar-menu.project.database')}</Title>
          </div>
          <div className='flex-1 overflow-auto mt-4 flex flex-col'>
            <MainBlock extraclass='flex-1 flex flex-col overflow-auto'>
              <div className='flex items-center justify-between'>
                <div>
                  <div className='text-sm md:text-base font-bold'>{t('project.database.all-tables')} ({dbInfos.length})</div>
                </div>
                <div className='gap-4 flex my-1 flex-row'>
                  {dbInfos.length > 0 && <button onClick={() => setShowDeleteDatabasesAlert(true)} disabled={selected.length === 0} className='delete-databases-button bg-gray-100 disabled:opacity-30 rounded-lg p-2 px-4 text-xs'>{t('project.database.delete-databases')}</button>}
                  <button onClick={() => setShowAddDatabaseAlert(true)} className='add-database-button bg-primary text-white rounded-lg p-2 px-4 text-xs'>{t('project.database.add-database')}</button>
                </div>
              </div>
              <div className='flex flex-col flex-1 overflow-auto mt-2'>
                <Table
                  data={dbInfos}
                  setData={setDbInfos}
                  selected={selected}
                  setSelected={setSelected}
                  attrKey={t('table-attr.general.name')}
                  isKeyBotton
                  onClick={(e, item) => navigate(`/project/${props.project.id}/database/${item[t('table-attr.general.name')]}`)}
                  attributes={[{ name: t('table-attr.general.name'), type: 'string' }, { name: t('table-attr.general.created-at'), type: 'string' }]}
                />
                {dbInfos.length === 0 && <div className='flex-1 flex items-center justify-center font-light text-sm'>{t('project.database.no-database')}</div>}
              </div>
            </MainBlock>
          </div>
        </div>

        {/* delete databases */}
        {showDeleteDatabasesAlert && <AlertWrapper>
          <Alert
            extraclass='delete-databases-alert'
            title={t('project.database.delete-databases')}
            maxWidth={480}
            onClose={() => setShowDeleteDatabasesAlert(false)}
            body={<div className='mx-1'>
              <div>{t('project.database.delete-database-message')}</div>
            </div>}
            bottom={<div className='flex'>
              <button onClick={deleteDatabases} disabled={selected.length === 0 || isDeleting} className='disabled:bg-gray-300 bg-primary text-white text-sm p-2 px-8 rounded-lg'>{isDeleting ? <Loading /> : t('general.delete')}</button>
            </div>}
          />
        </AlertWrapper>}

        {/* add database */}
        {showAddDatabaseAlert && <AlertWrapper>
          <Alert
            extraclass='add-database-alert'
            title={t('project.database.add-database')}
            maxWidth={400}
            onClose={() => setShowAddDatabaseAlert(false)}
            body={<div className='flex flex-col gap-2'>
              <div>
                <label className='text-xs text-gray-400 m-1'>{t('general.name')}<span className='text-xs ml-2'>({t('home.console.project.new-project.name-description')})</span></label>
                <Input autoFocus value={newDatabaseName} onChange={(e) => setNewDatabaseName(e.target.value)} />
                {databases.find(p => p.name === newDatabaseName) && <p className='text-xs text-red-400 m-1'>{t('home.console.project.new-project.name-exists')}</p>}
              </div>
            </div>}
            bottom={<div>
              {hasReachedLimit() && <div className='text-xs m-1 text-red-400'>{t('project.database.add-database.limit')}</div>}
              <div className='flex'><button onClick={addNewDatabase} disabled={hasReachedLimit() || isAdding || !!databases.find(p => p.name === newDatabaseName) || !isValidServiceName(newDatabaseName)} className='flex items-center justify-center bg-primary disabled:bg-gray-300 text-white px-8 py-2 rounded-lg text-sm'>{isAdding ? <Loading /> : t('general.add')}</button></div>
            </div>}
          />
        </AlertWrapper>}
      </>
  )
}

export default Database